import * as React from 'react';

import checkMark from '@sorpetaler/ui/src/images/checkmark-grey.png';
import { useTranslate } from '@sorpetaler/ui/src/utils/translation/useTranslation';

import * as styles from './DoorProgressIndicator.module.less';

interface IDoorProgressIndicatorProps {
  activeStep: number;
  numberOfSteps: number;
  onStepClick?: (step: number) => void;
}

export const DoorProgressIndicator: React.FunctionComponent<IDoorProgressIndicatorProps> = ({
  activeStep,
  numberOfSteps,
  onStepClick,
}) => {
  const { t } = useTranslate();
  const stepsArray = Array(numberOfSteps).fill('').map((_, i) => i + 1);

  const handleStepClick = React.useCallback(
    (stepNumber: number) => {
      // Only allow navigation to steps that have already been visited
      // Users cannot jump ahead to future steps without completing previous ones
      if (onStepClick && stepNumber <= activeStep && stepNumber !== activeStep) {
        onStepClick(stepNumber);
      }
    },
    [onStepClick, activeStep]
  );

  return (
    <div className={styles.container}>
      <div className={styles.textContainer}>
        <span className={styles.text}>{t('in3Steps')} {t('toThe')}</span>
        <span className={styles.textHighlighted}>{t('newFrontDoor')}</span>
      </div>
      <div className={styles.stepsContainer}>
        {stepsArray.map((stepNumber) => {
          const isCompleted = stepNumber < activeStep;
          const isActive = stepNumber === activeStep;
          const isDisabled = !!onStepClick && stepNumber > activeStep;
          const isInteractable = !!onStepClick && stepNumber < activeStep;

          const circleClasses = [
            styles.stepCircle,
            isCompleted ? styles.completed : '',
            isActive ? styles.active : '',
            !isCompleted && !isActive ? styles.inactive : '',
            onStepClick ? styles.clickable : '',
            isDisabled ? styles.disabled : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <React.Fragment key={stepNumber}>
              <div
                className={circleClasses}
                onClick={
                  isInteractable ? () => handleStepClick(stepNumber) : undefined
                }
                role={isInteractable ? 'button' : undefined}
                tabIndex={isInteractable ? 0 : -1}
              >
                {isCompleted ? (
                  <img src={checkMark} alt="check-mark" className={styles.checkmarkImage} />
                ) : (
                  stepNumber
                )}
              </div>
              {stepNumber < numberOfSteps && (
                <div
                  className={`${styles.stepBar} ${
                    stepNumber < activeStep ? styles.stepBarCompleted : ''
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

